import { EMPTY_STRING, KEY_BOARD } from 'constants/general';
import { useEffect, useRef, useCallback, useState } from 'react';
import { t } from 'services/i18n.service';
import { useSlowSnapScroll } from 'hooks/useSlowSnapScroll';
import { removeLeadingZerosFromNumbers } from 'utils/helpers';
import Select from '../Select';
import {
  TIME_WHEEL_PICKER_DEFAULTS,
  TIME_WHEEL_PICKER_SNAP_SCROLL_DEBOUNCE_MS,
  TimeWheelPickerColumnType,
  TimeWheelPickerSize,
  TimeWheelPickerVariant
} from 'constants/time-wheel-picker';

interface WheelColumnProps {
  items: string[];
  value: string;
  className?: string;
  onChange?: (arg: string) => void;
  type: TimeWheelPickerColumnType;
  size?: TimeWheelPickerSize;
  variant?: TimeWheelPickerVariant;
  skeleton?: boolean;
}

const NON_DIGIT_PATTERN = /\D/g;
const MAX_TIME_PART_DIGITS = 2;

const validateInput = (val: string, type: TimeWheelPickerColumnType) => {
  if (type === 'hour') {
    return !val || /^([1-9]|1[0-2])$/.test(val);
  }
  if (type === 'minute') {
    return !val || /^([0-5]?\d)$/.test(val);
  }
  if (type === 'period') {
    return /^(AM|PM)$/i.test(val);
  }
  return false;
};

const formatTimePart = (value: string, type: TimeWheelPickerColumnType) => {
  const updatedValue =
    type === 'period' ? value.toUpperCase() : value.padStart(2, '0');

  return value ? updatedValue : EMPTY_STRING;
};

const initInputState = {
  isEditing: false,
  value: EMPTY_STRING,
  hasError: false
};

const getSelectSize = (size: TimeWheelPickerSize) => {
  switch (size) {
    case 'small':
      return 'sm';
    case 'medium':
      return 'md';
    default:
      return 'lg';
  }
};

// Size variants for styling
const sizeClasses = {
  small: {
    container: '~w-8/12 ~h-6/8',
    input: '~w-8/12 ~h-6/8 ~text-xs/sm',
    button: '~p-0.5/1 ~h-6/8 ~text-xs/sm focus:outline-[0.016rem]'
  },
  medium: {
    container: '~w-16/20 ~h-10/16',
    input: '~w-16/20 ~h-10/16 ~text-sm/base',
    button: '~p-1/1.5 ~h-10/16 ~text-base/lg focus:outline-[0.031rem]'
  },
  large: {
    container: '~w-20/24 ~h-12/20',
    input: '~w-20/24 ~h-12/20 ~text-base/lg',
    button: '~p-1.5/2 ~h-12/20 ~text-lg/xl focus:outline-1'
  }
};

// Variant colors
const variantClasses = {
  primary: {
    selectedText: 'text-primary-900 dark:text-white',
    unselectedText: 'text-gray-600 dark:text-secondary-dark-700',
    outline: 'outline-primary-500 dark:outline-primary-dark-500',
    placeholder: 'placeholder-primary-200 dark:placeholder-secondary-dark-600'
  },
  default: {
    selectedText: 'text-gray-800 dark:text-secondary-dark-900',
    unselectedText: 'text-gray-500 dark:text-secondary-dark-700',
    outline: 'outline-gray-500 dark:outline-secondary-dark-500',
    placeholder: 'placeholder-gray-400 dark:placeholder-secondary-dark-600'
  }
};

const WheelColumn = ({
  items,
  value,
  onChange,
  className,
  type,
  size = TIME_WHEEL_PICKER_DEFAULTS.SIZE,
  variant = TIME_WHEEL_PICKER_DEFAULTS.VARIANT
}: WheelColumnProps) => {
  const inputNumberRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [inputState, setInputState] = useState(initInputState);
  const [columnIntersecting, setColumnIntersecting] = useState(
    () => typeof IntersectionObserver === 'undefined'
  );
  const suppressScrollSnapCommitRef = useRef(false);

  useEffect(() => {
    if (inputState.isEditing) {
      setColumnIntersecting(false);
      return;
    }

    const el = containerRef.current;
    if (!el) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setColumnIntersecting(true);
      return;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        setColumnIntersecting(
          Boolean(entry?.isIntersecting && entry.intersectionRatio > 0)
        );
      },
      { root: null, threshold: [0, 0.01] }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inputState.isEditing]);

  useEffect(() => {
    if (inputState.isEditing && type !== 'period') {
      inputNumberRef.current?.focus();
    }
  }, [inputState.isEditing, type]);

  useEffect(() => {
    if (inputState.isEditing) {
      return;
    }

    const selectedItem = itemRefs.current[value];
    const container = containerRef.current;

    if (!selectedItem || !container) {
      return;
    }

    suppressScrollSnapCommitRef.current = true;
    selectedItem.scrollIntoView({ block: 'center', behavior: 'auto' });

    const slackMs = TIME_WHEEL_PICKER_SNAP_SCROLL_DEBOUNCE_MS + 50;

    const scheduleRelease = () => {
      window.setTimeout(() => {
        suppressScrollSnapCommitRef.current = false;
      }, slackMs);
    };

    const scrollEndSupported = 'onscrollend' in container;

    if (scrollEndSupported) {
      container.addEventListener('scrollend', scheduleRelease, { once: true });
    }

    const fallbackId = window.setTimeout(() => {
      suppressScrollSnapCommitRef.current = false;
    }, slackMs * 3);

    return () => {
      container.removeEventListener('scrollend', scheduleRelease);
      window.clearTimeout(fallbackId);
      suppressScrollSnapCommitRef.current = false;
    };
  }, [value, inputState.isEditing]);

  const commitSnapValue = useCallback(
    (newValue: string) => {
      if (newValue !== value) {
        onChange?.(newValue);
      }
    },
    [onChange, value]
  );

  useSlowSnapScroll(containerRef, itemRefs, commitSnapValue, {
    enabled: columnIntersecting,
    suppressScrollCommitRef: suppressScrollSnapCommitRef
  });

  const getItemId = useCallback(
    (item: string, index: number) => `wheel-option-${item}-${index}`,
    []
  );

  const isPeriodType = type === 'period';

  const handleOnblur = () => {
    setInputState((prev) => ({ ...prev, isEditing: !inputState.value }));
    onChange?.(formatTimePart(inputState.value, type));
  };

  if (inputState.isEditing) {
    return (
      <div className='flex flex-col items-center'>
        {isPeriodType ? (
          <Select
            options={items.map((item) => ({ label: item, value: item }))}
            value={value}
            onChange={(selectedValue) => {
              onChange?.(selectedValue);
              setInputState(initInputState);
            }}
            onClose={() => setInputState(initInputState)}
            size={getSelectSize(size)}
            variant={variant === 'primary' ? 'brand' : 'filled'}
            className={`${sizeClasses[size].input} ~ml-0.5/1`}
            isFullWidth={false}
            defaultOpen
          />
        ) : (
          <input
            ref={inputNumberRef}
            // A text input with numeric mode preserves raw keystrokes; a
            // `type='number'` input reports intermediate/leading-zero states
            // (e.g. `00`) as an empty string, which blanked the typed value.
            type='text'
            inputMode='numeric'
            value={inputState.value}
            onFocus={(event) => event.target.select()}
            onChange={({ target: { value: latestValue } }) => {
              const digits = latestValue
                .replace(NON_DIGIT_PATTERN, EMPTY_STRING)
                .slice(0, MAX_TIME_PART_DIGITS);
              if (validateInput(digits, type)) {
                setInputState((prev) => ({
                  ...prev,
                  value: digits,
                  hasError: !digits
                }));
                onChange?.(formatTimePart(digits, type));
              }
            }}
            onBlur={handleOnblur}
            onKeyDown={(event) => {
              event.key === KEY_BOARD.ENTER.KEY && handleOnblur();
            }}
            maxLength={MAX_TIME_PART_DIGITS}
            className={`${sizeClasses[size].input} ~mx-0.5/1 border rounded-md text-center outline-none ${variantClasses[variant].placeholder} ${inputState.hasError && 'border-red-500'} shadow bg-white dark:bg-secondary-dark-300 text-gray-900 dark:text-secondary-dark-900 border-gray-200 dark:border-secondary-dark-500`}
          />
        )}
      </div>
    );
  }

  const handleListboxKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = items.findIndex((item) => item === value);
    let nextIndex = currentIndex;
    let handled = false;

    switch (event.key) {
      case KEY_BOARD.ENTER.KEY:
      case KEY_BOARD.SPACE.KEY:
        onChange?.(items[currentIndex]);
        handled = true;
        break;
      case KEY_BOARD.ARROW_DOWN.KEY:
        nextIndex = Math.min(items.length - 1, currentIndex + 1);
        handled = true;
        break;
      case KEY_BOARD.ARROW_UP.KEY:
        nextIndex = Math.max(0, currentIndex - 1);
        handled = true;
        break;
      case KEY_BOARD.HOME.KEY:
        nextIndex = 0;
        handled = true;
        break;
      case KEY_BOARD.END.KEY:
        nextIndex = items.length - 1;
        handled = true;
        break;
      default: {
        return;
      }
    }

    if (handled) {
      event.preventDefault();
      if (
        [
          KEY_BOARD.ARROW_DOWN.KEY,
          KEY_BOARD.ARROW_UP.KEY,
          KEY_BOARD.HOME.KEY,
          KEY_BOARD.END.KEY
        ].includes(event.key) &&
        items[nextIndex] &&
        items[nextIndex] !== value
      ) {
        onChange?.(items[nextIndex]);
      }
    }
  };

  return (
    <div
      ref={containerRef}
      className={`flex flex-col items-center ${sizeClasses[size].container} overflow-y-scroll scrollbar-none scroll-smooth snap-y snap-mandatory shadow bg-white dark:bg-secondary-dark-200 ~py-2/3 ${className}`}
      role='listbox'
      aria-label={t('accessibility.selectable_options')}
      aria-activedescendant={getItemId(
        value,
        items.findIndex((item) => item === value)
      )}
      tabIndex={0}
      onKeyDown={handleListboxKeyDown}>
      {items.map((item, index) => (
        <button
          key={`${item}-${index}`}
          id={getItemId(item, index)}
          ref={(el) => (itemRefs.current[item] = el)}
          className={`snap-center ${sizeClasses[size].button} cursor-pointer transition-colors duration-200 font-mono rounded ${variantClasses[variant].outline} ${
            item === value
              ? `${variantClasses[variant].selectedText} font-bold`
              : variantClasses[variant].unselectedText
          }`}
          onClick={() => {
            if (item === value) {
              setInputState({
                hasError: false,
                value: removeLeadingZerosFromNumbers(value),
                isEditing: true
              });
            } else {
              onChange?.(item);
            }
          }}
          role='option'
          aria-selected={item === value}>
          {item}
        </button>
      ))}
    </div>
  );
};

export default WheelColumn;
